import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { OrdersService, AggregatedItemCodeRequest, ItemCodeRequest, WarrantyCodeOrderRequest, ProductsService } from '@src/app/shared/backend/clients';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { SessionService } from '@src/app/shared/services/session.service';

@Component({
    selector: 'app-add-code-order',
    templateUrl: './add-code-order.component.html',
    styleUrl: './add-code-order.component.scss',
    host: {
        class: 'app-add-code-order'
    },
    standalone: false
})
export class AddCodeOrderComponent extends BaseViewComponent implements OnInit {

  hasSerialisationSettings = true;

  comboData: ComboData = new ComboData(async (filter) => await firstValueFrom(this.productsService.apiProductsFilterComboPost(filter)));

  public codeOrderForm: FormGroup = this.fb.group({
    codeOrderType: ['warranty-codes', [Validators.required]],
    quantity: ['', [Validators.required]],
    products: this.fb.array([])
  });

  constructor(
    private router: Router,
    private translate: TranslateService,
    private sessionService: SessionService,
    private toasterService: ToasterService,
    private codeOrdersService: OrdersService,
    private productsService: ProductsService,
    private fb: FormBuilder
  ) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    const companySettings = await this.sessionService.getCompanySettings();

    this.hasSerialisationSettings =
      !!companySettings?.serialisationSettings?.length;

    if (!this.hasSerialisationSettings) {
      return;
    }

    await this.initComboData();

    this.codeOrderForm.get('codeOrderType')?.valueChanges.subscribe(value => {
      this.onCodeOrderTypeChange(value);
    });
  }

  private async initComboData() {
    await this.comboData.getData(null, 0);
  }

  async onLazyLoadComboData(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.comboData.getData(search, activePage);
  }

  getComboTotalPages(): number {
    const totalRecords = this.comboData?.totalRecords ?? 0;
    const pageSize = Number(this.comboData?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  get productsFormArray(): FormArray {
    return this.codeOrderForm.get('products') as FormArray;
  }

  getProductControl(i: number) {
    return this.productsFormArray.at(i)?.get('product') as FormControl;
  }

  getQuantityControl(i: number) {
    return this.productsFormArray.at(i)?.get('quantity') as FormControl;
  }

  addProduct() {
    const productGroup = this.fb.group({
      product: [[], Validators.required],
      quantity: ['', Validators.required]
    });

    this.productsFormArray.push(productGroup);
    this.updateValidatorsForForm(this.codeOrderForm.get('codeOrderType')?.value);
  }

  removeProduct(i: number) {
    this.productsFormArray.removeAt(i);
  }

  onCodeOrderTypeChange(codeOrderType: string) {
    const quantityControl = this.codeOrderForm.get('quantity');
    const productsFormArray = this.codeOrderForm.get('products') as FormArray;

    productsFormArray.clear();
    quantityControl?.reset();

    this.updateValidatorsForForm(codeOrderType);
  }

  updateValidatorsForForm(codeOrderType: string) {
    const quantityControl = this.codeOrderForm.get('quantity');

    if (codeOrderType === 'item-codes') {
      quantityControl?.clearValidators();
      quantityControl?.setValue(null);
    } else {
      quantityControl?.setValidators([Validators.required]);
      quantityControl?.setValue(null);
    }

    quantityControl?.updateValueAndValidity();

    this.productsFormArray.controls.forEach((productControl) => {
      const productControlField = productControl.get('product');
      const quantityControlField = productControl.get('quantity');

      if (codeOrderType === 'item-codes') {
        productControlField?.setValidators([Validators.required]);
        quantityControlField?.setValidators([Validators.required]);
      } else if (codeOrderType === 'warranty-codes' || codeOrderType === 'aggregated-product-codes') {
        productControlField?.clearValidators();
        quantityControlField?.clearValidators();
      }

      productControlField?.updateValueAndValidity();
      quantityControlField?.updateValueAndValidity();
    });
  }

  get isFormValid(): boolean {
    const codeOrderType = this.codeOrderForm.get('codeOrderType')?.value;

    const isGeneralFormValid = this.codeOrderForm.valid && this.productsFormArray.valid;

    if (codeOrderType === 'item-codes') {
      const hasValidProduct = this.productsFormArray.controls.some(productControl => {
        const productControlValue = productControl.get('product')?.value || [];
        const quantityControlValue = productControl.get('quantity')?.value;

        return productControlValue.length > 0 && quantityControlValue != null && quantityControlValue > 0;
      });

      return isGeneralFormValid && hasValidProduct;
    }

    return isGeneralFormValid;
  }

  async submitForm() {
    const codeOrderTypeControl = this.codeOrderForm.get('codeOrderType');
    const codeOrderType = codeOrderTypeControl ? codeOrderTypeControl.value : null;

    const quantityControl = this.codeOrderForm.get('quantity');
    const quantity = quantityControl ? quantityControl.value : null;

    let payload: WarrantyCodeOrderRequest | AggregatedItemCodeRequest | ItemCodeRequest;

    switch (codeOrderType) {
      case 'warranty-codes':
        payload = {
          quantity: quantity
        } as WarrantyCodeOrderRequest;

        try {
          await firstValueFrom(this.codeOrdersService.apiOrdersCreateWarrantyCodesPost(payload));
          this.router.navigate(['/code-orders']);
        } catch {
          this.toasterService.show('error', this.translate.instant('CODE_ORDER_CREATE.TOAST_ERROR_TITLE_CODE_REQUEST_FAILED'), this.translate.instant('CODE_ORDER_CREATE.TOAST_ERROR_MESSAGE_CODE_REQUEST_FAILED'));
        }
        break;

      case 'aggregated-product-codes':
        payload = {
          quantity: quantity
        } as AggregatedItemCodeRequest;

        try {
          await firstValueFrom(this.codeOrdersService.apiOrdersCreateAggregatedProductCodesPost(payload));
          this.router.navigate(['/code-orders']);
        } catch {
          this.toasterService.show('error', this.translate.instant('CODE_ORDER_CREATE.TOAST_ERROR_TITLE_CODE_REQUEST_FAILED'), this.translate.instant('CODE_ORDER_CREATE.TOAST_ERROR_MESSAGE_CODE_REQUEST_FAILED'));
        }
        break;

      case 'item-codes': {
        quantityControl?.clearValidators();
        quantityControl?.updateValueAndValidity();

        const productIds: Record<string, number> = {};

        this.productsFormArray.controls.forEach((productControl) => {
          const product = productControl.get('product')?.value || [];
          const productId = product?.[0];
          const quantity = productControl.get('quantity')?.value;

          if (productId != null && quantity != null) {
            productIds[productId] = quantity;
          }
        });

        payload = {
          productIds: productIds
        } as ItemCodeRequest;

        try {
          await firstValueFrom(this.codeOrdersService.apiOrdersCreateItemCodesPost(payload));
          this.router.navigate(['/code-orders']);
        } catch {
          this.toasterService.show('error', this.translate.instant('CODE_ORDER_CREATE.TOAST_ERROR_TITLE_CODE_REQUEST_FAILED'), this.translate.instant('CODE_ORDER_CREATE.TOAST_ERROR_MESSAGE_CODE_REQUEST_FAILED'));
        }
        break;
      }

      default:
        console.warn('Unknown order type:', codeOrderType);
        break;
    }
  }
}
